import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

// ═══════════════════════════════════════════════════════════════
// AI COACH PANEL - Premium chat interface for Day 4+
// Daily usage limit, premium feature
// ═══════════════════════════════════════════════════════════════

export const AICoachPanel = ({ onClose, dayNumber, lessonContext }) => {
  const { api } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Cześć! Jestem Twoim AI Coachem. 🎯\n\nPomogę Ci zrozumieć dzisiejszą lekcję i odpowiem na pytania o promptach. Pytaj śmiało!`
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageInfo, setUsageInfo] = useState({ remaining: null, limit: 10 });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const res = await api.post('/ai-coach', {
        message: userMessage,
        conversation_history: conversationHistory,
        day_number: dayNumber,
        lesson_context: lessonContext
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response 
      }]);
      
      if (res.data.remaining_uses !== undefined) {
        setUsageInfo(prev => ({ ...prev, remaining: res.data.remaining_uses }));
      }
    } catch (e) {
      console.error('AI Coach error:', e);
      const errorMsg = e.response?.data?.detail || 'Przepraszam, wystąpił błąd. Spróbuj ponownie.';
      
      if (e.response?.status === 429) {
        setError('Wykorzystałeś dzienny limit wiadomości. Wróć jutro!');
      } else if (e.response?.status === 403) {
        setError('Ta funkcja wymaga subskrypcji PRO lub aktywnego darmowego dostępu.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Chat Panel */}
      <div 
        className="relative w-full sm:max-w-lg h-[85vh] sm:h-[600px] sm:rounded-2xl overflow-hidden flex flex-col animate-slideUp"
        style={{
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(10, 15, 30, 0.98))',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #0ea5e9, #10b981)',
                boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
              }}
            >
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">AI Coach</h3>
              <p className="text-xs text-slate-400">Dzień {dayNumber}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {usageInfo.remaining !== null && (
              <span className="text-xs text-slate-400 bg-slate-800 px-2 py-1 rounded-full">
                {usageInfo.remaining} pytań dziś
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-coach-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-sky-500 text-white rounded-br-md'
                    : 'bg-white/10 text-slate-200 rounded-bl-md'
                }`}
                style={{
                  boxShadow: msg.role === 'user' 
                    ? '0 4px 15px rgba(14, 165, 233, 0.2)'
                    : 'none'
                }}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
              
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-slate-300" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Myślę...</span>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div 
              className="flex items-center gap-2 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
            >
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div 
          className="p-4 border-t"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Zadaj pytanie o prompty..."
              disabled={isLoading}
              className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 transition-colors"
              data-testid="coach-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="bg-sky-500 hover:bg-sky-600 rounded-xl px-4 h-12 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 text-center mt-2">
            AI Coach pomoże Ci zrozumieć lekcję
          </p>
        </div>
      </div>
    </div>
  );
};

export default AICoachPanel;
